/**
 * (c) 2021, Micro:bit Educational Foundation and contributors
 *
 * SPDX-License-Identifier: MIT
 *
 * Keep static imports minimal. Vite's entry `assets/index-*.js` only includes what
 * this file imports synchronously.
 *
 * First paint: App + Redux store + i18n only. RobotService (SDK, deploy helpers,
 * stream graph) loads after `root.render` so Projects UI is not blocked on it.
 * CONNECT clicked during that import window stays in Redux; RobotService replays
 * it when construction finishes (`_replayPendingConnectionCommand`).
 */
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import '@sphero/blockly/styles.css';
import { registerBuildInfo } from './buildInfo';
import { registerPwaUpdateReload } from './registerPwa';

registerPwaUpdateReload();
registerBuildInfo(window);

declare const window: Window &
  typeof globalThis & {
    robotService?: import('./robot/robot-service').default;
  };

(async () => {
  const root = createRoot(document.getElementById('root')!);
  try {
    const [{ default: App }, { default: store }, { ensureI18nInitialized }] = await Promise.all([
      import('./App'),
      import('./redux/Store'),
      import('./i18n'),
    ]);

    await ensureI18nInitialized();

    root.render(
      <StrictMode>
        <App />
      </StrictMode>
    );

    const [{ default: RobotService }, { preloadAssetConfig }] = await Promise.all([
      import('./robot/robot-service'),
      import('./robot/robot-asset-manager'),
    ]);
    const robotService = new RobotService(store);
    window.robotService = robotService;
    preloadAssetConfig().catch(() => {});
    import('./robots/bprLogUpload/bprLogUploadStartup')
      .then(({ startBprLogUpload }) => startBprLogUpload({ store, robotService }))
      .catch((error) => console.warn('[robot-log-upload] Failed to start', error));
  } catch (err) {
    console.error('Failed to bootstrap app:', err);
    const bootstrapErrorDefault = 'Error initializing application';
    let bootstrapErrorMessage = bootstrapErrorDefault;
    try {
      const [{ ensureI18nInitialized }, i18nextModule] = await Promise.all([import('./i18n'), import('i18next')]);
      await ensureI18nInitialized();
      bootstrapErrorMessage = i18nextModule.default.t('app.bootstrap.error', bootstrapErrorDefault);
    } catch {
      // Keep the English default if i18n itself failed to load.
    }
    root.render(<div>{bootstrapErrorMessage}</div>);
  }
})();
